import { Component, EventEmitter, Input, OnInit, Output } from '@angular/core';
import { FormBuilder, FormGroup, Validators } from '@angular/forms';
import { TicketForwardUserMappingService } from 'src/app/services/ticketforwardusermapping.service';
import { AlertService } from 'src/app/services/alert.service';

@Component({
  selector: 'app-ticketforwardusermappingassign',
  templateUrl: './ticketforwardusermappingassign.component.html',
  styleUrls: ['./ticketforwardusermappingassign.component.scss']
})
export class TicketforwardusermappingassignComponent implements OnInit {
  @Input() actionId: any;
  @Input() resultdata: any;
  @Output() backComp = new EventEmitter<any>();

  form!: FormGroup;

  // Role dropdowns are UI-only filters - never saved. Two independent copies so
  // filtering the source-user list doesn't affect the forward-to-user list, or vice versa.
  roleList: any[] = [];
  filterRoleList: any[] = [];
  targetRoleList: any[] = [];
  filterTargetRoleList: any[] = [];
  sourceRoleId: any = 0;
  targetRoleId: any = 0;

  // Full lists feed mat-select-filter's [array]; the filter* copies hold its output.
  sourceUserList: any[] = [];
  filterSourceUserList: any[] = [];
  assignedUserList: any[] = [];
  filterAssignedUserList: any[] = [];

  // name + role for every user we've seen, so selected chips stay labelled even
  // after the target-role filter moves to a different role.
  private userMeta: Map<number, { name: string, roleName: string }> = new Map();

  constructor(
    private fb: FormBuilder,
    private ticketForwardUserMappingService: TicketForwardUserMappingService,
    private alertService: AlertService,
  ) { }

  ngOnInit(): void {
    this.initialValidators();
    this.getRoleLookup();
    this.loadSourceUsers();
    this.loadAssignedUsers();

    if (this.actionId == 1 || this.actionId == 2) {
      this.ticketForwardUserMappingService.getByUser(this.resultdata.userId, true).subscribe((mapping: any) => {
        if (mapping) {
          // Seed metadata from the saved mapping so each chip shows its role immediately,
          // before/independent of whatever role filter is active.
          (mapping.forwardToUsers || []).forEach((item: any) => this.userMeta.set(item.assignedUserId, {
            name: item.assignedUserName,
            roleName: item.assignedUserRoleName
          }));

          this.sourceRoleId = mapping.userRoleId || 0;
          this.loadSourceUsers();

          this.form.patchValue({
            userId: this.resultdata.userId,
            assignedUserIds: mapping.forwardToUsers?.map((item: any) => item.assignedUserId) || []
          });
        }
      });
    }
  }

  initialValidators() {
    this.form = this.fb.group({
      'userId': [this.actionId == 1 || this.actionId == 2 ? this.resultdata.userId : '', Validators.required],
      'assignedUserIds': [[], Validators.required],
    });
  }

  getRoleLookup() {
    this.ticketForwardUserMappingService.getRoles(true).subscribe((res: any) => {
      this.roleList = res || [];
      this.filterRoleList = this.roleList.slice();
      this.targetRoleList = this.roleList.slice();
      this.filterTargetRoleList = this.roleList.slice();
    });
  }

  // Value1 carries the role name from the lookup endpoint.
  private cacheUserMeta(list: any[]) {
    (list || []).forEach(u => this.userMeta.set(u.key, { name: u.value, roleName: u.value1 }));
  }

  loadSourceUsers() {
    if (!this.sourceRoleId) {
      this.sourceUserList = [];
      this.filterSourceUserList = [];
      return;
    }
    this.ticketForwardUserMappingService.getUsersByRole(this.sourceRoleId, true).subscribe((res: any) => {
      this.cacheUserMeta(res);
      this.sourceUserList = res || [];
      this.filterSourceUserList = this.sourceUserList.slice();
    });
  }

  private buildOptionFromId(id: any) {
    const meta = this.userMeta.get(id);
    return { key: id, value: meta?.name || '', value1: meta?.roleName || '' };
  }
  loadAssignedUsers() {
    const selectedIds: any[] = this.form?.value?.assignedUserIds || [];
    if (!this.targetRoleId) {
      this.assignedUserList = selectedIds.map(id => this.buildOptionFromId(id));
      this.filterAssignedUserList = this.assignedUserList.slice();
      return;
    }
    this.ticketForwardUserMappingService.getUsersByRole(this.targetRoleId, true).subscribe((res: any) => {
      this.cacheUserMeta(res);
      const fetched = res || [];
      const fetchedIds = new Set(fetched.map((u: any) => u.key));
      const missingSelected = selectedIds.filter(id => !fetchedIds.has(id)).map(id => this.buildOptionFromId(id));
      this.assignedUserList = fetched.concat(missingSelected);
      this.filterAssignedUserList = this.assignedUserList.slice();
    });
  }
 
  // Narrows the Source User dropdown to users holding the selected role (server round-trip).
  onSourceRoleChange() {
    this.loadSourceUsers();
  }

 
  onTargetRoleChange() {
    this.loadAssignedUsers();
  }

  
  get selectedForwardToUsers() {
    const ids: any[] = this.form?.value?.assignedUserIds || [];
    return ids.map(id => ({
      id,
      name: this.userMeta.get(id)?.name || '',
      roleName: this.userMeta.get(id)?.roleName || ''
    }));
  }

  removeForwardToUser(userId: number) {
    if (this.actionId == 2) return;
    const ids: any[] = this.form.value.assignedUserIds || [];
    this.form.controls['assignedUserIds'].setValue(ids.filter(id => id !== userId));
    this.form.controls['assignedUserIds'].markAsDirty();
  }

  back() {
    const msg = { show: true };
    this.backComp.emit(msg);
  }

  Update() {
    if (this.form.valid) {
      const data = {
        userId: this.form.value.userId,
        assignedUserIds: this.form.value.assignedUserIds
      };
      this.ticketForwardUserMappingService.save(data).subscribe((res: any) => {
        if (res && res.isSuccess) {
          this.alertService.success(this.actionId == 0 ? 'Ticket Forward Mapping Saved Successfully' : 'Ticket Forward Mapping Updated Successfully');
          this.back();
        } else if (res && res.failures) {
          this.alertService.error(res.failures.toString());
        }
      });
    } else {
      this.form.markAllAsTouched();
    }
  }
}
